import React, { useRef, useEffect } from 'react';
import './ViolinFingerPositionDiagram.css';

function ViolinFingerPositionDiagram({ scale = 3, scaleType = 'Major', rootNote = 'A', highlight = false, detectedPitch }) {
  const canvasRef = useRef(null);

  useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      const scales = {
          Major: {
              'A': ['A', 'B', 'C#', 'D', 'E', 'F#', 'G#'],
              'A#': ['A#', 'C', 'D', 'D#', 'F', 'G', 'A'],
              'B': ['B', 'C#', 'D#', 'E', 'F#', 'G#', 'A#'],
              'C': ['C', 'D', 'E', 'F', 'G', 'A', 'B'],
              'C#': ['C#', 'D#', 'F', 'F#', 'G#', 'A#', 'C'],
              'D': ['D', 'E', 'F#', 'G', 'A', 'B', 'C#'],
              'D#': ['D#', 'F', 'G', 'G#', 'A#', 'C', 'D'],
              'E': ['E', 'F#', 'G#', 'A', 'B', 'C#', 'D#'],
              'F': ['F', 'G', 'A', 'A#', 'C', 'D', 'E'],
              'F#': ['F#', 'G#', 'A#', 'B', 'C#', 'D#', 'F'],
              'G': ['G', 'A', 'B', 'C', 'D', 'E', 'F#'],
              'G#': ['G#', 'A#', 'C', 'C#', 'D#', 'F', 'G'],
          },
          Minor: {
              'A': ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
              'A#': ['A#', 'C', 'C#', 'D#', 'F', 'F#', 'G#'],
              'B': ['B', 'C#', 'D', 'E', 'F#', 'G', 'A'],
              'C': ['C', 'D', 'D#', 'F', 'G', 'G#', 'A#'],
              'C#': ['C#', 'D#', 'E', 'F#', 'G#', 'A', 'B'],
              'D': ['D', 'E', 'F', 'G', 'A', 'A#', 'C'],
              'D#': ['D#', 'F', 'F#', 'G#', 'A#', 'B', 'C#'],
              'E': ['E', 'F#', 'G', 'A', 'B', 'C', 'D'],
              'F': ['F', 'G', 'G#', 'A#', 'C', 'C#', 'D#'],
              'F#': ['F#', 'G#', 'A', 'B', 'C#', 'D', 'E'],
              'G': ['G', 'A', 'A#', 'C', 'D', 'D#', 'F'],
              'G#': ['G#', 'A#', 'B', 'C#', 'D#', 'E', 'F#']
          }
      };

      const stringLabels = ['G3', 'D4', 'A4', 'E5'];
      const fingerLabels = [
          ['G#3', 'A3', 'A#3', 'B3', 'C4', 'C#4', 'D4'],
          ['D#4', 'E4', 'F4', 'F#4', 'G4', 'G#4', 'A4'],
          ['A#4', 'B4', 'C5', 'C#5', 'D5', 'D#5', 'E5'],
          ['F5', 'F#5', 'G5', 'G#5', 'A5', 'A#5', 'B5']
      ];

      function extractNoteAndOctave(augmentedNote) {
          const note = augmentedNote.slice(0, -1);
          const octave = parseInt(augmentedNote.slice(-1));
          return { note, octave };
      }

      // Set canvas dimensions
      canvas.width = 150 * scale;
      canvas.height = 300 * scale;

      // Clear the canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw violin background
      ctx.fillStyle = '#f4f4f4'; // Setting to off-white/light gray
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      if (highlight) {
        // Drawing a rounded rectangle as the border around the canvas
        ctx.strokeStyle = "#007BFF"; // blue border
        ctx.lineWidth = 5;  // Adjust border thickness if needed
        ctx.lineJoin = "round"; // This makes the corners rounded
        ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);
      }

      // Draw the strings
      const yStart = 30 * scale;
      const lineWidth = 2 * Math.sqrt(scale);
      const stringColors = ['#000', '#000', '#000', '#000']; // All strings are black
      stringColors.forEach((color, index) => {
          ctx.fillStyle = color;
          const xPosition = (canvas.width / 5) * (index + 1);
          ctx.fillRect(xPosition, yStart, lineWidth, canvas.height - 20);
      });

      // Draw violin end
      ctx.fillStyle = '#000'; // Black color
      const xPosition0 = canvas.width / 5;
      const xPositionN = (canvas.width / 5) * 3;
      ctx.fillRect(xPosition0, yStart, xPositionN, lineWidth);
      ctx.fillRect(xPosition0, yStart + 10 * scale, xPositionN, lineWidth);

      // Debug: Draw detectedPitch data.
      // ctx.fillText(detectedPitch.note, 10, yStart/2);
      // ctx.fillText(detectedPitch.centsOff, 10, yStart);

      // Draw string labels above each string
      const chosenScale = scales[scaleType][rootNote];

      ctx.font = `${14 * scale}px Arial`;  // Slightly bigger font for string labels
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#000"; // black text

      for (let i = 0; i < 4; i++) {
          const { note } = extractNoteAndOctave(stringLabels[i]);
          if (!chosenScale.includes(note)) {
            continue;  // Skip the note if not in the chosen scale
          }
          const stringXPosition = (lineWidth / 2) + (canvas.width / 5) * (i + 1);
          const stringYPosition = yStart / 2;
          ctx.fillText(note, stringXPosition, stringYPosition);
      }

      // Draw circles on each string
      const rootNoteFillColor = '#ADD8E6';
      const activeNoteFillColor = '#007BFF';
      const circleSpacing = (canvas.height - 30) / 8;
      const circleRadius = (0.7 * circleSpacing) / 2;
      
      for (let i = 0; i < 4; i++) {
          const stringXPosition = lineWidth / 2 + (canvas.width / 5) * (i + 1);
          
          for (let j = 0; j < 7; j++) {
              const augmentedNote = fingerLabels[i][j];
              const { note } = extractNoteAndOctave(augmentedNote);
              if (!chosenScale.includes(note)) {
                continue;  // Skip the note if not in the chosen scale
              }

              const circleYPosition = yStart + lineWidth + 1.2 * (2 * circleRadius) + (circleSpacing * j);
              
              // Draw the circle
              ctx.beginPath();
              ctx.arc(stringXPosition, circleYPosition, circleRadius, 0, Math.PI * 2, true);
              if (detectedPitch && augmentedNote === detectedPitch.augmentedNote) {
                  ctx.fillStyle = activeNoteFillColor;
              } else if (note === rootNote) {
                  ctx.fillStyle = rootNoteFillColor;
              } else {
                  ctx.fillStyle = "#f4f4f4"; // Default white fill
              }
              ctx.fill();
              ctx.strokeStyle = "#000"; // black border
              ctx.lineWidth = lineWidth;
              ctx.stroke();
          }
      }

      // Draw labels on each circle
      ctx.font = `${12 * scale}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#000"; // black text

      for (let i = 0; i < 4; i++) {
          const stringXPosition = (lineWidth / 2) + (canvas.width / 5) * (i + 1);
          
          for (let j = 0; j < 7; j++) {
              const { note } = extractNoteAndOctave(fingerLabels[i][j]);
              if (!chosenScale.includes(note)) {
                continue;  // Skip the note if not in the chosen scale
              }
              const circleYPosition = yStart + lineWidth + 1.2 * (2 * circleRadius) + (circleSpacing * j);
              
              // Draw the text
              ctx.fillText(note, stringXPosition, circleYPosition);
          }
      }

      // Draw the magenta lines.
      const centsScaleFactor = .4;
      function drawMagentaLineAt(xPosition, yPosition) {
        ctx.beginPath();
        ctx.strokeStyle = "#E83E8C"; // Magenta color
        ctx.moveTo(xPosition - circleRadius, yPosition);
        ctx.lineTo(xPosition + circleRadius, yPosition);
        ctx.lineWidth = lineWidth;
        ctx.stroke();
      }

      for (let i = 0; i < 4; i++) {
          const stringXPosition = (lineWidth / 2) + (canvas.width / 5) * (i + 1);
          const { octave } = extractNoteAndOctave(stringLabels[i]);
          // Draw magenta line on open string.
          if (detectedPitch && stringLabels[i] === detectedPitch.augmentedNote) {
            const offset = detectedPitch.centsOff * centsScaleFactor * scale;
            drawMagentaLineAt(stringXPosition, 3/4 * yStart + offset);
          }

          // Draw magenta line on circle.
          for (let j = 0; j < 7; j++) {
              const circleYPosition = yStart + lineWidth + 1.2 * (2 * circleRadius) + (circleSpacing * j);

              if (detectedPitch && fingerLabels[i][j] === detectedPitch.augmentedNote) {
                  const offset = detectedPitch.centsOff * centsScaleFactor * scale;
                  drawMagentaLineAt(stringXPosition, circleYPosition + offset);
              }
          }
      }
  }, [scale, scaleType, rootNote, highlight, detectedPitch]);

  return <canvas className="inlineCanvas" ref={canvasRef} />;
}

export default ViolinFingerPositionDiagram;